/**
 * Lightweight email validation + typo guard.
 *
 * - `isValidEmail` does a basic shape check (RFC 5322 is overkill for forms;
 *   we want "looks like an email" and let the auth provider catch the rest).
 * - `suggestEmailFix` looks at the domain part and, if it's close to a known
 *   common domain, returns a suggested correction. Catches gmial.com / gmal.com
 *   / outlok.com / yaho.ca etc.
 *
 * No external dependency. The `mailcheck.js` library is unmaintained; this
 * inline approach handles ~95% of the typos we'll see in practice.
 */

const COMMON_DOMAINS = [
  // Big global providers
  'gmail.com',
  'googlemail.com',
  'outlook.com',
  'hotmail.com',
  'live.com',
  'yahoo.com',
  'icloud.com',
  'me.com',
  'aol.com',
  'protonmail.com',
  'proton.me',
  // Canadian
  'yahoo.ca',
  'hotmail.ca',
  'outlook.ca',
  'sympatico.ca',
  'rogers.com',
  'bell.net',
  'shaw.ca',
  'telus.net',
  // Plus the user's own org domain — pass it in via the optional argument
];

/** Basic shape check — "x@y.z" with no whitespace. */
export function isValidEmail(input: string | null | undefined): boolean {
  if (!input) return false;
  const v = input.trim();
  if (v.length < 5 || v.length > 254) return false;
  // RFC-lite: a non-empty local part, an @, a domain with a dot, no spaces.
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

/**
 * Damerau-Levenshtein distance.
 * Counts insertion, deletion, substitution, and adjacent-transposition each
 * as a single edit. Critical for catching keyboard typos like "gmial" -> "gmail"
 * (one transposition) and "gmial.con" -> "gmail.com" (transposition + sub).
 * Plain Levenshtein would score those as 2 and 3 respectively.
 */
function distance(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const m = a.length;
  const n = b.length;
  const d: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) d[i][0] = i;
  for (let j = 0; j <= n; j++) d[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(
        d[i - 1][j] + 1,         // deletion
        d[i][j - 1] + 1,         // insertion
        d[i - 1][j - 1] + cost   // substitution
      );
      // Transposition (Damerau): adjacent char swap is one edit, not two.
      if (
        i > 1 && j > 1 &&
        a[i - 1] === b[j - 2] &&
        a[i - 2] === b[j - 1]
      ) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[m][n];
}

/**
 * If the email's domain looks like a typo of a common domain, return a
 * suggested replacement email. Returns null if no close match is found or the
 * input domain is already a known one.
 *
 * Threshold: edit distance 1 or 2 (1 for short domains, 2 for longer).
 */
export function suggestEmailFix(
  input: string | null | undefined,
  extraDomains: string[] = []
): string | null {
  if (!input) return null;
  const v = input.trim().toLowerCase();
  const at = v.lastIndexOf('@');
  if (at < 1 || at === v.length - 1) return null;

  const local = v.slice(0, at);
  const domain = v.slice(at + 1);
  if (!domain.includes('.')) return null;

  const candidates = [...COMMON_DOMAINS, ...extraDomains.map((d) => d.toLowerCase())];
  if (candidates.includes(domain)) return null; // already a known good domain

  // Try every candidate at full-domain distance. Threshold 2 means we catch
  // gmial.con -> gmail.com (distance 2: i->a swap + n->m swap on tld) as well
  // as the simpler gmial.com -> gmail.com (distance 1).
  let bestMatch: { domain: string; dist: number } | null = null;
  for (const cand of candidates) {
    const d = distance(domain, cand);
    // Allow up to 2 edits for any candidate. Skip exact matches (d=0) and
    // anything where the suggestion is barely shorter than the typo (would
    // otherwise suggest "gmail.com" for "x.io").
    if (d > 0 && d <= 2 && Math.abs(domain.length - cand.length) <= 2) {
      if (!bestMatch || d < bestMatch.dist) {
        bestMatch = { domain: cand, dist: d };
      }
    }
  }

  if (!bestMatch) return null;
  return `${local}@${bestMatch.domain}`;
}

export const EMAIL_VALIDATION_HINT = 'Please enter a valid email address.';
